/* Phase 8 Slice 3 — Industry benchmarks panel.
 *
 * Collapsible card under the cockpit funnel that lists every benchmark
 * row applicable to the tenant's industry + region. Aggregate-only —
 * no per-tenant data is ever displayed (architectural commitment, see
 * ADR-0039). Each row shows its provenance (source + reference).
 */
import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Card, CardContent, CardHeader, CardTitle,
} from "../ui/card";
import { ChevronDown, ChevronUp, Info } from "lucide-react";
import { api } from "../../lib/api";
import { useT } from "../../lib/i18n";

function fmtBenchmarkValue(metric, value) {
  if (value === null || value === undefined) return "—";
  if (metric === "roas") return `${Number(value).toFixed(1)}×`;
  if (metric === "cpl_eur" || metric === "cpc_eur" || metric === "cpm_eur") {
    return `€${Number(value).toFixed(0)}`;
  }
  if (metric === "ctr" || metric.endsWith("_rate")) {
    return `${(Number(value) * 100).toFixed(1)}%`;
  }
  return String(value);
}

export default function BenchmarksPanel() {
  const t = useT();
  const [open, setOpen] = useState(false);
  const { data, isLoading } = useQuery({
    queryKey: ["cockpit-benchmarks-panel"],
    queryFn: async () => {
      const r = await api.get("/cockpit/benchmarks");
      return r.data;
    },
    staleTime: 5 * 60 * 1000,
  });

  const rows = data?.rows || [];
  const empty = !isLoading && rows.length === 0;

  return (
    <Card data-testid="benchmarks-panel">
      <CardHeader>
        <button
          type="button"
          data-testid="benchmarks-panel-toggle"
          className="flex w-full items-center justify-between gap-2
                          text-left"
          onClick={() => setOpen((v) => !v)}
        >
          <CardTitle className="flex items-center gap-2">
            {t("benchmarks.title", "Branchen-Benchmarks")}
            <span
              data-testid="benchmarks-panel-count"
              className="text-xs text-muted-foreground font-normal"
            >
              {data?.industry
                ? `· ${data.industry} · n≥${data.k_anonymity_threshold ?? 10}`
                : ""}
            </span>
          </CardTitle>
          {open
            ? <ChevronUp className="h-4 w-4 text-muted-foreground" />
            : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
        </button>
      </CardHeader>
      {open && (
        <CardContent className="pt-0">
          {empty ? (
            <p className="text-sm text-muted-foreground italic">
              {t("benchmarks.empty",
                  "Keine Benchmarks für diese Branche verfügbar.")}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table
                data-testid="benchmarks-panel-table"
                className="w-full text-xs tabular-nums"
              >
                <thead className="text-muted-foreground">
                  <tr className="text-left border-b border-border">
                    <th className="py-2 pr-2 font-medium">
                      {t("benchmarks.col_metric", "Kennzahl")}
                    </th>
                    <th className="py-2 px-2 font-medium">
                      {t("benchmarks.col_platform", "Plattform")}
                    </th>
                    <th className="py-2 px-2 font-medium">
                      {t("benchmarks.col_kind", "Kampagnenart")}
                    </th>
                    <th className="py-2 px-2 font-medium text-right">
                      {t("benchmarks.col_value", "Wert")}
                    </th>
                    <th className="py-2 px-2 font-medium">
                      {t("benchmarks.col_aggregate", "Aggregat")}
                    </th>
                    <th className="py-2 px-2 font-medium">
                      {t("benchmarks.col_source", "Quelle")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id}
                          data-testid={`benchmarks-row-${r.id}`}
                          className="border-b border-border/40">
                      <td className="py-1.5 pr-2 font-medium">{r.metric}</td>
                      <td className="py-1.5 px-2">{r.platform}</td>
                      <td className="py-1.5 px-2">{r.campaign_kind}</td>
                      <td className="py-1.5 px-2 text-right">
                        {fmtBenchmarkValue(r.metric, r.value)}
                      </td>
                      <td className="py-1.5 px-2 uppercase text-[10px]
                                              text-muted-foreground">
                        {r.aggregate_kind}
                      </td>
                      <td className="py-1.5 px-2 text-[10px]
                                              text-muted-foreground"
                            title={r.source_reference || ""}>
                        <span className="inline-flex items-center gap-1">
                          <Info className="h-3 w-3" />
                          {r.source}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="mt-2 text-[10px] text-muted-foreground italic">
                {t("benchmarks.privacy_note",
                    "Aggregate-only. Keine tenant-spezifischen Daten enthalten "
                    + "(k-Anonymität ≥ 10).")}
              </p>
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
}
